import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthUser, homeRouteFor, logout } from '../../services/authApi';
import { fetchMyQuotes, countByStatus, Quote } from '../../services/carrierApi';
import {
  StatGrid, PaginatedQuoteTable, CapabilityList, icons,
} from '../../components/dashboard/DashboardKit';

/* SubCarrier user — UserType "SubCarrier".
   Sees only the quotes their carrier assigned to them. The list comes from our
   API (/api/SubCarrier/my-quotes), which calls NowCerts server side. */

const CarrierUserDashboard: React.FC = () => {
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [search, setSearch] = useState('');

  // Assignments change, so every visit (and every Retry) fetches fresh.
  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      setQuotes(await fetchMyQuotes());
    } catch (err: any) {
      if (err?.status === 401) { logout(); navigate('/login'); return; }
      if (err?.status === 403) { navigate(homeRouteFor(getAuthUser())); return; }
      setQuotes([]);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const completed =
    countByStatus(quotes, 'Bound') + countByStatus(quotes, 'Approved');
  const inProgress =
    countByStatus(quotes, 'In Review') + countByStatus(quotes, 'Pending');

  const stats = [
    {
      label: 'My Quotes',
      value: quotes.length,
      accent: '#6f42c1', bg: '#f5f3ff', icon: icons.doc,
      hint: 'Assigned to you',
    },
    {
      label: 'In Progress',
      value: inProgress,
      accent: '#d97706', bg: '#fffbeb', icon: icons.progress,
      hint: 'Pending or in review',
    },
    {
      label: 'Completed',
      value: completed,
      accent: '#059669', bg: '#ecfdf5', icon: icons.check,
      hint: 'Bound or approved',
    },
  ];

  const filtered = quotes.filter(q =>
    `${q.id} ${q.insuredName}`.toLowerCase().includes(search.toLowerCase())
  );

  const emptyLabel = isLoading ? 'Loading your quotes…'
    : loadFailed ? (
      <>
        Couldn’t load quotes, try again.{' '}
        <button type="button" className="assign-retry" onClick={load}>Retry</button>
      </>
    )
    : quotes.length > 0 ? 'No records found'
    : 'No quotes assigned to you yet. Your carrier can assign quotes to you.';

  return (
    <div className="dashboard">
      <StatGrid stats={stats} />

      <PaginatedQuoteTable
        quotes={filtered}
        title="My Assigned Quotes"
        subtitle="Submissions assigned to you — newest first"
        emptyLabel={emptyLabel}
        showDetails
        onViewDetails={q =>
          navigate(`/my-quotes/quote/${encodeURIComponent(q.insuredDatabaseId || q.databaseId || q.id)}`, {
            state: { quoteId: q.id, status: q.status, company: q.insuredName },
          })}
        headerRight={
          <div className="db-table-card__actions">
            <div className="search-box search-box--sm">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search ID or insured…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
          </div>
        }
      />

      <div className="cap-grid">
        <CapabilityList
          title="What you can do"
          items={[
            'View assigned submissions (own carrier only)',
            'Manage assigned quotes',
            'Endorsements',
            'Update submission information',
            'View limited reports',
          ]}
        />
      </div>
    </div>
  );
};

export default CarrierUserDashboard;
